import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { InventoryItemDto, InventoryKind } from "@mmm/contracts";
import { getInventory } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import { SortableColumnHeader, type SortDirection } from "../../components/molecules/SortableColumnHeader.js";
import { LoadingRows } from "../../components/ui/LoadingPlaceholders.js";
import { PageTitle } from "../../components/ui/PageTitle.js";
import { SearchInput } from "../../components/ui/SearchInput.js";
import { EmptyState, ErrorState, type MessageKey, Select, useLanguage, useT } from "@mmm/ui";
type KindFilter = InventoryKind | "all";
type SortColumn = "kind" | "title" | "subject" | "chapter" | "updated";

/**
 * Singular, because each cell labels ONE row. The filter dropdown below uses
 * plural labels, which is why the two lists differ — "Maps" in a Type cell
 * describing a single map is exactly the confusion this screen had.
 */
const KIND_LABELS: Record<InventoryKind, MessageKey> = {
  subject: "common.subject",
  chapter: "inventory.chapter",
  map: "nav.map",
  test: "eventType.test",
  task: "inventory.task",
  event: "inventory.event",
};

/** Plural, for "show me all of this kind". */
const KIND_FILTER_LABELS: Record<InventoryKind, MessageKey> = {
  subject: "nav.subjects",
  chapter: "chapters.heading",
  map: "inventory.maps",
  test: "nav.tests",
  task: "nav.tasks",
  event: "inventory.events",
};

const KIND_ORDER: InventoryKind[] = ["subject", "chapter", "map", "test", "task", "event"];

/** Router state read by useBackTo on the destination screen. */
const BACK_TO_INVENTORY = { backTo: "/inventory" } as const;

/**
 * The user's complete inventory as a sortable table.
 *
 * Every row names four independent things — what it is, what it's called, and
 * which subject/chapter it sits under — so they get their own columns rather
 * than being concatenated into one line. Search/sort/filter are client-side:
 * `getInventory` returns only the caller's own records, so re-sorting is
 * instant and needs no round trip.
 */
export function InventoryScreen() {
  const inventoryQuery = useQuery(getInventory, undefined);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [sort, setSort] = useState<SortColumn>("updated");
  const [direction, setDirection] = useState<SortDirection>("desc");
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = useT();

  const items = inventoryQuery.data;

  function sortBy(column: SortColumn) {
    if (column === sort) {
      setDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSort(column);
    // Dates read newest-first by default; text reads A–Z. Starting a date
    // column ascending would open on the oldest row, which nobody wants.
    setDirection(column === "updated" ? "desc" : "asc");
  }

  const visible = useMemo(() => {
    if (!items) return [];
    const needle = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      if (kind !== "all" && item.kind !== kind) return false;
      if (!needle) return true;
      // Parent names are searchable too, so "Biology" finds that subject's
      // chapters and maps and not only the subject row itself.
      return [item.title, item.subjectTitle, item.chapterTitle]
        .filter((value): value is string => typeof value === "string")
        .some((value) => value.toLowerCase().includes(needle));
    });

    const factor = direction === "asc" ? 1 : -1;
    return filtered.slice().sort((a, b) => factor * compare(a, b, sort, t));
  }, [items, search, kind, sort, direction, t]);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(language, { year: "numeric", month: "short", day: "numeric" }),
    [language]
  );

  return (
    <div>
      <PageTitle title={t("inventory.title")} />

      <div className="mmm-inventory-controls">
        <SearchInput value={search} onChange={setSearch} placeholder={t("subjects.searchPlaceholder")} />

        <label className="mmm-inventory-control">
          <span className="mmm-inventory-control-label">{t("inventory.typeLabel")}</span>
          <Select value={kind} onChange={(event) => setKind(event.target.value as KindFilter)}>
            <option value="all">{t("common.all")}</option>
            {KIND_ORDER.map((value) => (
              <option key={value} value={value}>
                {t(KIND_FILTER_LABELS[value])}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {inventoryQuery.error && !inventoryQuery.waiting ? (
        <ErrorState title={t("inventory.loadFailed")} onRetry={inventoryQuery.retry} />
      ) : !items ? (
        <div className="mmm-card">
          <LoadingRows rows={6} />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          title={items.length === 0 ? t("inventory.emptyTitle") : t("inventory.noMatch")}
          message={items.length === 0 ? t("inventory.emptyMessage") : t("subjects.noMatchMessage")}
        />
      ) : (
        <>
          <p className="mmm-inventory-count">
            {t("inventory.countShown", { shown: visible.length, total: items.length })}
          </p>
          <div className="mmm-table-wrap">
            <table className="mmm-table">
              <thead>
                <tr>
                  <SortableColumnHeader
                    label={t("inventory.typeLabel")}
                    active={sort === "kind"}
                    direction={direction}
                    onSort={() => sortBy("kind")}
                  />
                  <SortableColumnHeader
                    label={t("common.name")}
                    active={sort === "title"}
                    direction={direction}
                    onSort={() => sortBy("title")}
                  />
                  <SortableColumnHeader
                    label={t("common.subject")}
                    active={sort === "subject"}
                    direction={direction}
                    onSort={() => sortBy("subject")}
                  />
                  <SortableColumnHeader
                    label={t("inventory.chapter")}
                    active={sort === "chapter"}
                    direction={direction}
                    onSort={() => sortBy("chapter")}
                  />
                  <SortableColumnHeader
                    label={t("inventory.updated")}
                    active={sort === "updated"}
                    direction={direction}
                    onSort={() => sortBy("updated")}
                    align="end"
                  />
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr
                    key={`${item.kind}:${item.id}`}
                    className="mmm-table-row mmm-table-row--clickable"
                    // Clicking anywhere on the row opens it — the whole row
                    // reads as one thing, so requiring a hit on the name text
                    // is a needlessly small target. The name stays a real
                    // <a>, which is what keyboard users tab to and what
                    // middle-click/"open in new tab" acts on; this handler is
                    // a pointer convenience layered on top, not a replacement.
                    // `backTo` gives the destination an in-app back control
                    // pointing here, so returning to the inventory doesn't
                    // depend on the browser's back button.
                    onClick={() => navigate(item.href, { state: BACK_TO_INVENTORY })}
                  >
                    <td className="mmm-table-td mmm-table-td--kind">{t(KIND_LABELS[item.kind])}</td>
                    <td className="mmm-table-td">
                      <Link
                        to={item.href}
                        state={BACK_TO_INVENTORY}
                        className="mmm-inventory-link"
                        // Without this the row handler fires too and navigates
                        // twice, pushing a duplicate history entry.
                        onClick={(event) => event.stopPropagation()}
                      >
                        {item.title}
                      </Link>
                    </td>
                    {/* An em dash rather than an empty cell: "this row has no
                        subject" and "this column failed to render" should not
                        look the same. */}
                    <td className="mmm-table-td mmm-table-td--muted">{item.subjectTitle ?? "—"}</td>
                    <td className="mmm-table-td mmm-table-td--muted">{item.chapterTitle ?? "—"}</td>
                    <td className="mmm-table-td mmm-table-td--end mmm-table-td--muted">
                      {dateFormat.format(new Date(item.updatedAt))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function compare(a: InventoryItemDto, b: InventoryItemDto, column: SortColumn, t: (key: MessageKey) => string): number {
  switch (column) {
    case "kind":
      // Sorted by the translated label, so the order matches what's on screen
      // rather than the internal enum order.
      return t(KIND_LABELS[a.kind]).localeCompare(t(KIND_LABELS[b.kind]));
    case "title":
      return a.title.localeCompare(b.title);
    case "subject":
      return (a.subjectTitle ?? "").localeCompare(b.subjectTitle ?? "");
    case "chapter":
      return (a.chapterTitle ?? "").localeCompare(b.chapterTitle ?? "");
    case "updated":
    default:
      return a.updatedAt.localeCompare(b.updatedAt);
  }
}
